import { useEffect, useRef, useState } from "react";

export interface ElementSize {
    width: number;
    height: number;
}

/**
 * Observes an element's dimensions and returns live width/height updates.
 * @param elementId - Id of the element to measure.
 * @returns Current element size, defaulting to zeros before observation.
 */
export default function useResize(elementId: string) {
   const [size, setSize] = useState<ElementSize>({ width: 0, height: 0 });

     const resizeObserverRef = useRef<ResizeObserver | null>(null);
     const mutationObserverRef = useRef<MutationObserver | null>(null);
     const observedElementRef = useRef<HTMLElement | null>(null);
   
    /**
     * Stops observing the current element and resets observer references.
     */
    const cleanupResizeObserver = () => {
       if (resizeObserverRef.current && observedElementRef.current) {
         resizeObserverRef.current.unobserve(observedElementRef.current);
       }
       resizeObserverRef.current?.disconnect();
       resizeObserverRef.current = null;
       observedElementRef.current = null;
     };
   
    /**
     * Starts observing the provided element for resize events.
     * @param el - Element to observe for size changes.
     */
    const setupResizeObserver = (el: HTMLElement) => {
       cleanupResizeObserver();
       observedElementRef.current = el;
       resizeObserverRef.current = new ResizeObserver(entries => {
         for (const entry of entries) {
           const { width, height } = entry.contentRect;
           setSize({ width, height });
         }
       });
       resizeObserverRef.current.observe(el);
     };
   
     useEffect(() => {
      /**
       * Finds the target element and ensures the resize observer is attached.
       */
      const checkAndObserve = () => {
         const el = document.getElementById(elementId);
         if (el && el !== observedElementRef.current) {
           setupResizeObserver(el);
         } else if (!el && observedElementRef.current) {
           // Element is gone — clean up
           cleanupResizeObserver();
         }
       };
   
       checkAndObserve(); // Check immediately
   
       mutationObserverRef.current = new MutationObserver(checkAndObserve);
       mutationObserverRef.current.observe(document.body, {
         childList: true,
         subtree: true,
       });
   
       return () => {
         mutationObserverRef.current?.disconnect();
         cleanupResizeObserver();
       };
     }, [elementId]);
   
     return size;
}


